import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styled from 'styled-components';

import { MaxLengthCounter } from '@components/max-length-counter';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { Question, QuestionAnswer } from 'api/ai-builder/requests';

type AiBuilderQuestionCardProps = {
  question: Question;
  questionIndex: number;
  totalQuestions: number;
  answer: QuestionAnswer;
  onAnswerChange: (answer: QuestionAnswer) => void;
};

export const OTHER_OPTION_LABEL = 'Other';

export function AiBuilderQuestionCard({
  question,
  questionIndex,
  totalQuestions,
  answer,
  onAnswerChange,
}: AiBuilderQuestionCardProps) {
  const { t } = useScopedTranslate(locale);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const otherInputRef = useRef<HTMLTextAreaElement>(null);

  const allOptions = useMemo(() => {
    return question.options.map((option) => ({ ...option, id: option.label }));
  }, [question.options]);

  const isSelected = useCallback(
    (optionId: string) => {
      if (optionId === OTHER_OPTION_LABEL) {
        return answer.customText.length > 0 || answer.selectedOptions.includes(OTHER_OPTION_LABEL);
      }
      return answer.selectedOptions.includes(optionId);
    },
    [answer]
  );

  const isOtherSelected = isSelected(OTHER_OPTION_LABEL);

  const handleToggleOption = useCallback(
    (optionId: string) => {
      const newAnswer = { ...answer };

      if (question.isMultiSelect) {
        if (optionId === OTHER_OPTION_LABEL) {
          if (isOtherSelected) {
            newAnswer.selectedOptions = newAnswer.selectedOptions.filter(
              (id) => id !== OTHER_OPTION_LABEL
            );
            newAnswer.customText = '';
          } else {
            newAnswer.selectedOptions = [...newAnswer.selectedOptions, OTHER_OPTION_LABEL];
            requestAnimationFrame(() => otherInputRef.current?.focus());
          }
        } else {
          if (newAnswer.selectedOptions.includes(optionId)) {
            newAnswer.selectedOptions = newAnswer.selectedOptions.filter((id) => id !== optionId);
          } else {
            newAnswer.selectedOptions = [...newAnswer.selectedOptions, optionId];
          }
        }
      } else {
        if (optionId === OTHER_OPTION_LABEL) {
          newAnswer.selectedOptions = [OTHER_OPTION_LABEL];
          requestAnimationFrame(() => otherInputRef.current?.focus());
        } else {
          newAnswer.selectedOptions = [optionId];
          newAnswer.customText = '';
        }
      }

      onAnswerChange(newAnswer);
    },
    [answer, question.isMultiSelect, isOtherSelected, onAnswerChange]
  );

  const handleCustomTextChange = useCallback(
    (text: string) => {
      const newAnswer = { ...answer, customText: text };

      if (text.length > 0 && !answer.selectedOptions.includes(OTHER_OPTION_LABEL)) {
        if (question.isMultiSelect) {
          newAnswer.selectedOptions = [...answer.selectedOptions, OTHER_OPTION_LABEL];
        } else {
          newAnswer.selectedOptions = [OTHER_OPTION_LABEL];
        }
      }

      onAnswerChange(newAnswer);
    },
    [answer, question.isMultiSelect, onAnswerChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const { key } = e;

      if (key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = (focusedIndex + 1) % allOptions.length;
        setFocusedIndex(nextIndex);
        optionRefs.current[nextIndex]?.focus();
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = (focusedIndex - 1 + allOptions.length) % allOptions.length;
        setFocusedIndex(prevIndex);
        optionRefs.current[prevIndex]?.focus();
      } else if (key === 'Enter' || key === ' ') {
        if (document.activeElement === otherInputRef.current) {
          return;
        }
        e.preventDefault();
        handleToggleOption(allOptions[focusedIndex].id);
      }
    },
    [focusedIndex, allOptions, handleToggleOption]
  );

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, allOptions.length);
  }, [allOptions.length]);

  return (
    <CardContainer onKeyDown={handleKeyDown}>
      <Text
        font="semibold12"
        text={t('questionProgress', {
          current: (questionIndex + 1).toString(),
          total: totalQuestions.toString(),
        })}
        color="textHelper"
      />

      <QuestionText>
        <Text font="regular16" text={question.question} color="textPrimary" />
      </QuestionText>

      <OptionsContainer role="listbox" aria-multiselectable={question.isMultiSelect}>
        {allOptions.map((option, index) => {
          const selected = isSelected(option.id);
          const isOther = option.id === OTHER_OPTION_LABEL;

          return (
            <OptionItem
              key={option.id}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              role="option"
              aria-selected={selected}
              tabIndex={index === focusedIndex ? 0 : -1}
              onClick={() => handleToggleOption(option.id)}
              onFocus={() => setFocusedIndex(index)}
              data-selected={selected}
            >
              <Stack orientation="horizontal" space={3} align="flex-start">
                <SelectionIndicator $isMulti={question.isMultiSelect} $isSelected={selected}>
                  {question.isMultiSelect && selected && <CheckIcon />}
                  {!question.isMultiSelect && selected && <RadioDot />}
                </SelectionIndicator>
                <OptionContent>
                  <Text font="semibold14" text={option.label} color="textPrimary" />
                  <Text font="regular12" text={option.description} color="textHelper" />
                </OptionContent>
              </Stack>
              {isOther && (
                <OtherInputContainer $isVisible={selected}>
                  <TextArea
                    ref={otherInputRef}
                    value={answer.customText}
                    placeholder={t('otherPlaceholder')}
                    onClick={(e) => e.stopPropagation()}
                    onChange={handleCustomTextChange}
                    maxLength={500}
                  />
                  <MaxLengthCounter currentLength={answer.customText.length} maxLength={500} />
                </OtherInputContainer>
              )}
            </OptionItem>
          );
        })}
      </OptionsContainer>
    </CardContainer>
  );
}

const CardContainer = styled.div({
  width: '100%',
});

const QuestionText = styled.div({
  marginBottom: pxToRem(16),
});

const OptionsContainer = styled(Stack)({
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(12),
  overflow: 'hidden',
});

const OptionItem = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: pxToRem(12),
  background: theme.color.backgroundPrimary,
  border: 'none',
  borderBottom: `1px solid ${theme.color.border10}`,
  textAlign: 'left',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',

  '&:last-child': {
    borderBottom: 'none',
  },

  '&:hover': {
    background: theme.color.backgroundLightDarkHover,
  },

  '&:focus': {
    outline: 'none',
    background: theme.color.backgroundLightDarkHover,
  },

  '&[data-selected="true"]': {
    background: `${theme.color.elementPurple80}10`,
  },
});

const SelectionIndicator = styled.div<{ $isMulti: boolean; $isSelected: boolean }>(
  ({ $isMulti, $isSelected }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: pxToRem(18),
    height: pxToRem(18),
    flexShrink: 0,
    marginTop: pxToRem(2),
    borderRadius: $isMulti ? pxToRem(4) : '50%',
    border: `2px solid ${$isSelected ? theme.color.elementPurple80 : theme.color.textHelper}`,
    background: $isSelected ? theme.color.elementPurple80 : 'transparent',
    transition: 'all 0.15s ease',
  })
);

const CheckIcon = styled.span({
  width: pxToRem(10),
  height: pxToRem(6),
  borderLeft: `2px solid ${theme.color.white100}`,
  borderBottom: `2px solid ${theme.color.white100}`,
  transform: 'rotate(-45deg) translateY(-1px)',
});

const RadioDot = styled.span({
  width: pxToRem(8),
  height: pxToRem(8),
  borderRadius: '50%',
  background: theme.color.white100,
});

const OptionContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(2),
  flex: 1,
  minWidth: 0,
});

const OtherInputContainer = styled.div<{ $isVisible: boolean }>(({ $isVisible }) => ({
  overflow: 'hidden',
  maxHeight: $isVisible ? pxToRem(200) : 0,
  opacity: $isVisible ? 1 : 0,
  transition: 'max-height 0.25s ease, opacity 0.2s ease, margin-top 0.25s ease',
  marginTop: $isVisible ? pxToRem(12) : 0,
}));

const locale = {
  en: {
    questionProgress: 'Question {current} of {total}',
    otherPlaceholder: 'Type your answer...',
  },
};
